import { createContext, useContext, useReducer, ReactNode } from 'react';
import produce from 'immer';

import {
  FilterGroupType,
  AppliedFilterType,
  FilterStateType,
  ModifiedFilterType,
} from '@insights_module/types/ContextualAndEntity';
import { FILTER_STATE, FILTERS_GROUP } from '@insights_module/constants/contextualAndEntity';

import { useAppleParams } from '@insights_module/hooks/useAppleParams';
import { useIABParams } from '@insights_module/hooks/useIABParams';
import { useGroupParams } from '@insights_module/hooks/useGroupParams';
import { unimplemented } from '@utils/commonUtils';

type FiltersContextKeys =
  | 'insights.contextual.filters.show_filter_menu'
  | 'insights.contextual.filters.selected_group'
  | 'insights.contextual.filters.modified';

type Action = {
  type: FiltersContextKeys;
  payload: string | boolean | null | ModifiedFilterType[];
};

export interface IFiltersContext {
  selectedGroup: FilterGroupType | null;
  showFiltersMenu: boolean;
  setSelectedGroup: (value: FilterGroupType) => void;
  setFiltersMenu: (value: boolean) => void;
  getFiltersByState: (value: FilterStateType) => { key: string; group: FilterGroupType }[];
  setModifiedFilters: (value: ModifiedFilterType[]) => void;
  appliedFilters: {
    group: FilterGroupType;
    param: { [key: string]: string };
  }[];
  modifiedFilters: ModifiedFilterType[];
}

const FILTERS_CONTEXT_KEYS = {
  SHOW_FILTER_MENU: 'insights.contextual.filters.show_filter_menu' as FiltersContextKeys,
  SELECT_FILTERS_GROUP: 'insights.contextual.filters.selected_group' as FiltersContextKeys,
  SET_MODIFIED_FILTER: 'insights.contextual.filters.modified' as FiltersContextKeys,
};

const initialState = {
  showFiltersMenu: false,
  selectedGroup: null,
  appliedFilters: [],
  modifiedFilters: [],
  setSelectedGroup: unimplemented,
  setFiltersMenu: unimplemented,
  getFiltersByState: function (): { key: string; group: FilterGroupType }[] {
    throw new Error('Function not implemented.');
  },
  setModifiedFilters: unimplemented,
};

const FiltersContext = createContext<IFiltersContext>(initialState);

const reducer = produce((draft: IFiltersContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case FILTERS_CONTEXT_KEYS.SHOW_FILTER_MENU:
      draft.showFiltersMenu = payload as boolean;
      break;
    case FILTERS_CONTEXT_KEYS.SELECT_FILTERS_GROUP:
      draft.selectedGroup = payload as FilterGroupType | null;
      break;
    case FILTERS_CONTEXT_KEYS.SET_MODIFIED_FILTER:
      draft.modifiedFilters = payload as ModifiedFilterType[];
      break;

    default:
      break;
  }
}, initialState);

const getAppliedParams = (
  group: FilterGroupType,
  params: { [key: string]: string }
): AppliedFilterType[] =>
  Object.entries(params)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value !== FILTER_STATE.ignore)
    .map((item) => {
      return {
        group,
        param: { [item[0]]: item[1] },
      };
    });

const getAppliedNameEntities = (params: { [key: string]: string[] }) => {
  const { include_named_entities } = params;
  const { exclude_named_entities } = params;

  return [
    ...include_named_entities.map((item) => ({
      group: 'named_entity',
      param: { [item]: 'includes' },
    })),
    ...exclude_named_entities.map((item) => ({
      group: 'named_entity',
      param: { [item]: 'excludes' },
    })),
  ];
};

const getAppliedLinkedNameEntities = (params: { [key: string]: string[] }) => {
  const { include_linked_named_entities } = params;
  const { exclude_linked_named_entities } = params;

  if (
    !Array.isArray(include_linked_named_entities) ||
    !Array.isArray(exclude_linked_named_entities)
  ) {
    return [];
  }

  return [
    ...include_linked_named_entities.map((item) => ({
      group: 'linked_named_entities',
      param: { [item]: 'includes' },
    })),
    ...exclude_linked_named_entities.map((item) => ({
      group: 'linked_named_entities',
      param: { [item]: 'excludes' },
    })),
  ];
};

export const FiltersProvider = ({ children }: { children: ReactNode }) => {
  const { appleParams, setAppleParams } = useAppleParams();
  const { iabParams } = useIABParams();
  const { groupParams } = useGroupParams();
  const [state, dispatch] = useReducer(reducer, initialState);

  const iabFilters = getAppliedParams(FILTERS_GROUP.IAB, iabParams);
  const appleFilters = getAppliedParams(FILTERS_GROUP.APPLE, appleParams);
  const namedEntityFilters = getAppliedNameEntities(groupParams as { [key: string]: string[] });
  const linkedNamedEntityFilters = getAppliedLinkedNameEntities(
    groupParams as { [key: string]: string[] }
  );

  const appliedFilters = [
    ...iabFilters,
    ...appleFilters,
    ...namedEntityFilters,
    ...linkedNamedEntityFilters,
  ] as AppliedFilterType[];

  const getFiltersByState = (val: FilterStateType) =>
    appliedFilters
      .filter((item: AppliedFilterType) => Object.values(item.param)[0] === val)
      .map((item: AppliedFilterType) => {
        return {
          key: Object.keys(item.param)[0],
          group: item.group,
        };
      });

  const setSelectedGroup = (payload: FilterGroupType) => {
    return dispatch({ type: FILTERS_CONTEXT_KEYS.SELECT_FILTERS_GROUP, payload });
  };
  const setFiltersMenu = (payload: boolean) => {
    return dispatch({ type: FILTERS_CONTEXT_KEYS.SHOW_FILTER_MENU, payload });
  };
  const setModifiedFilters = (payload: ModifiedFilterType[]) => {
    return dispatch({ type: FILTERS_CONTEXT_KEYS.SET_MODIFIED_FILTER, payload });
  };

  const value = {
    ...state,
    appliedFilters,
    setAppleParams,
    getFiltersByState,
    setSelectedGroup,
    setFiltersMenu,
    setModifiedFilters,
  };

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};

export const useFiltersContext = () => {
  return useContext(FiltersContext);
};
